import type {ComponentType} from 'svelte';
import {get, writable} from 'svelte/store';
import api from 'Utility/api';
import type {
  EntitySubType,
  EntityType,
} from 'Store/investorProfile/entityStore';
import {ApiTarget} from 'Types/api';

export type Address = {
  street_address: string;
  unit2: string;
  city: string;
  region: string;
  country: string;
  postal_code: string;
};

export type BaseProfile = {
  first_name?: string;
  last_name?: string;
  name?: string;
  suffix?: string;
  date_of_birth?: string;
  date?: string;
  taxpayer_id?: string;
  address?: Address;
  us_accredited_category?: string;
  phone_number?: string;
};

export type Profile = {
  id: number;
  type: EntityType;
  email: string;
  user_id: number;
  us_accredited_category: string | null;
  complete: boolean;
  owner_type: OwnerTypes;

  owner: Owner;

  account_holder?: BaseProfile;

  joint_type?: EntitySubType;
  primary_holder?: BaseProfile;
  joint_holder?: BaseProfile;

  corporation?: BaseProfile;
  signing_officer?: BaseProfile;

  trust_holder?: BaseProfile;
  trustee_holder?: BaseProfile;

  provider?: BaseProfile;
  beneficiary?: BaseProfile;
};

export enum OwnerTypes {
  USER = 'User',
  ENTERPRISE = 'Enterprise',
  COMPANY = 'Company',
}

export type Owner = {
  id: number;
  name: string;
  logo: string;
};

export type ProfileObject = {
  profile: Profile;
};

export type ProfileResponse = {
  items: ProfileObject[];
};

export enum EntityTypes {
  INDIVIDUAL = 'individual',
  JOINT = 'joint',
  CORPORATION = 'corporation',
  TRUST = 'trust',
  MANAGED = 'managed',
}

export enum EntityLabels {
  INDIVIDUAL = 'Myself/an individual',
  JOINT = 'Joint (more than one individual)',
  CORPORATION = 'Corporation',
  TRUST = 'Trust',
  MANAGED = 'IRA',
}

export enum EntitySubTypes {
  JOINT_TENANT = 'joint_tenant',
  TENANTS_IN_COMMON = 'tenants_in_common',
  COMMUNITY_PROPERTY = 'community_property',
}

export type EntityTypeItem = {
  step: string;
  type: string;
  icon: ComponentType;
  label: string;
  onClick: Function;
};

export const PRIMARY_ENTITY_PATH = {
  [EntityTypes.INDIVIDUAL]: 'account_holder',
  [EntityTypes.JOINT]: 'primary_holder',
  [EntityTypes.CORPORATION]: 'corporation',
  [EntityTypes.TRUST]: 'trust_holder',
  [EntityTypes.MANAGED]: 'beneficiary',
};

export const SECONDARY_ENTITY_PATH = {
  [EntityTypes.INDIVIDUAL]: '',
  [EntityTypes.JOINT]: 'joint_holder',
  [EntityTypes.CORPORATION]: '',
  [EntityTypes.TRUST]: 'trustee_holder',
  [EntityTypes.MANAGED]: 'provider',
};

const createProfilesStore = () => {
  const profilesStore = writable<Profile[]>([]);
  const {set, subscribe} = profilesStore;

  const filterByType = (type: EntityTypes): Profile[] =>
    get(profilesStore).filter(p => p.type === type);

  const isEmpty = (): boolean => !get(profilesStore).length;

  return {
    set,
    subscribe,
    fetchProfiles: async (dealId: number, userId: number): Promise<void> => {
      const url = `investor_profiles/${
        dealId && userId ? `${dealId}?user_id=${userId}` : ''
      }`;
      const responseData = await api.getJson<ProfileResponse>(
        ApiTarget.V1,
        url
      );

      set(responseData.items.map(i => i.profile));
    },
    fetchAllProfiles: async (userId: number): Promise<void> => {
      const url = `investor_profiles/?user_id=${userId}`;
      const responseData = await api.getJson<ProfileResponse>(
        ApiTarget.V1,
        url
      );

      const profileTypeOrder = {
        individual: 1,
        joint: 2,
        trust: 3,
        corporation: 4,
        managed: 5,
      };

      const sortedProfiles = responseData.items
        .map(i => i.profile)
        .sort((a, b) => {
          if (a.type === b.type) {
            return a.id - b.id;
          }
          return profileTypeOrder[a.type] - profileTypeOrder[b.type];
        });

      set(sortedProfiles);
    },
    filterByType,
    isEmpty,
  };
};

export const profilesStore = createProfilesStore();
